import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Eye, Info, TrendingUp, Lightbulb, Compass, ArrowUpRight, GraduationCap, Share2, Check } from "lucide-react";
import PillarRadar from "@/components/PillarRadar";
import TierBadge from "@/components/TierBadge";
import GemBadge from "@/components/GemBadge";
import DigitalSeal from "@/components/DigitalSeal";
import CategoryBenchmark from "@/components/CategoryBenchmark";
import { PILLARS, TIER_META, TIER_KEYS, TIER_BADGE_IMG, normalizeTier } from "@/constants";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// — demo analytics helpers (no PII; session-based) —
function getOrCreateSessionId() {
  try {
    let sid = localStorage.getItem("lala_demo_sid");
    if (!sid) {
      sid = (crypto.randomUUID && crypto.randomUUID()) || `s-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem("lala_demo_sid", sid);
    }
    return sid;
  } catch (e) {
    return `s-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }
}
function track(eventType, meta = {}, durationMs = null) {
  try {
    const payload = {
      session_id: getOrCreateSessionId(),
      event_type: eventType,
      timestamp: new Date().toISOString(),
      device: /iphone|android|mobile/i.test(navigator.userAgent) ? "mobile" : "desktop",
      viewport_w: window.innerWidth,
      viewport_h: window.innerHeight,
      referrer: document.referrer || null,
      duration_ms: durationMs,
      meta,
    };
    const url = `${BACKEND}/api/public/demo-event`;
    const body = new Blob([JSON.stringify(payload)], { type: "application/json" });
    // sendBeacon survives navigation; required for CTAs that navigate away (click_login_cta, click_back_landing, page_exit)
    if (navigator.sendBeacon && navigator.sendBeacon(url, body)) return;
    // Fallback: fetch with keepalive so the request survives unload
    fetch(url, {
      method: "POST",
      body: JSON.stringify(payload),
      headers: { "Content-Type": "application/json" },
      keepalive: true,
    }).catch(() => {});
  } catch (e) { /* no-op */ }
}
const PILLAR_ADVICE = {
  calidad: "Mantén certificaciones vigentes y cero no-conformidades de auditoría.",
  costo: "Acompaña ajustes de precio con datos de mercado y propuestas de eficiencia.",
  continuidad: "Cumple lead-time prometido y conserva el nivel de inventario pactado.",
  innovacion: "Lleva propuestas de mejora a tu comprador Lala (producto, proceso, digitalización).",
  sustentabilidad: "Documenta avances ESG y plan de reducción de huella. Subir 1 punto aquí es alcanzable.",
  relacion: "Documentación al día con tu comprador y respuesta clara a requerimientos de cumplimiento.",
};
const NEXT_TIER = { commercial: "challenge", challenge: "key", key: "partner", partner: null };

export default function DemoSupplier() {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);
  const startTimeRef = useRef(Date.now());
  const scrollFiredRef = useRef({ 25: false, 50: false, 75: false, 100: false });

  useEffect(() => {
    axios.get(`${BACKEND}/api/public/demo`).then((r) => setData(r.data)).catch(() => setData({ error: true }));
    // Track page view + scroll + page exit
    track("page_view");
    startTimeRef.current = Date.now();

    const onScroll = () => {
      const el = document.documentElement;
      const total = el.scrollHeight - el.clientHeight;
      if (total <= 0) return;
      const pct = Math.round((el.scrollTop / total) * 100);
      [25, 50, 75, 100].forEach((b) => {
        if (pct >= b && !scrollFiredRef.current[b]) {
          scrollFiredRef.current[b] = true;
          track(`scroll_${b}`);
        }
      });
    };
    const onExit = () => {
      track("page_exit", {}, Date.now() - startTimeRef.current);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("beforeunload", onExit);
    document.addEventListener("visibilitychange", () => { if (document.visibilityState === "hidden") onExit(); });
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("beforeunload", onExit);
    };
  }, []);

  const supplier = data?.supplier;
  const latest = supplier?.latest_evaluation;
  const trend = useMemo(() => (data?.evaluations || []).map((e) => ({ period: e.period, score: e.score })), [data]);
  const recommendations = useMemo(() => {
    if (!latest?.pillars) return [];
    return PILLARS.map((p) => ({ ...p, value: latest.pillars[p.key] ?? 0 })).sort((a, b) => a.value - b.value).slice(0, 2);
  }, [latest]);
  const tierKey = normalizeTier(supplier?.current_tier);
  const tierMeta = TIER_META[tierKey];
  const nextKey = NEXT_TIER[tierKey];
  const nextMeta = nextKey ? TIER_META[nextKey] : null;

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const share = async () => {
    track("click_share");
    if (navigator.share) {
      try { await navigator.share({ title: "Lala Elite — Demo del proveedor", url: shareUrl }); return; } catch (e) {}
    }
    await navigator.clipboard.writeText(shareUrl);
    setCopied(true); setTimeout(() => setCopied(false), 2400);
  };

  if (data?.error) return <div className="px-6 py-20 text-center font-hand text-2xl text-[var(--lala-red,#E0162B)]">No se pudo cargar el demo público.</div>;
  if (!data) return <div className="px-6 py-20 text-center font-hand text-2xl text-[var(--lala-blue)]">Cargando demo…</div>;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-10" data-testid="demo-supplier-page">
      {/* Demo banner */}
      <div className="mb-6 sm:mb-8 rounded-2xl bg-[var(--lala-blue)] text-white p-4 sm:p-5 flex items-center justify-between flex-wrap gap-3" data-testid="demo-banner">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center"><Eye size={18} /></div>
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold opacity-80">Demo público · sin login</div>
            <div className="font-display text-lg sm:text-xl">Así ve un proveedor su lugar en la familia LALA Elite</div>
            <div className="text-xs opacity-75 hidden sm:block">Datos ficticios de "Lácteos del Bajío". El proveedor real solo consulta — no captura nada.</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={share} data-testid="share-demo-btn" className="bg-white/15 hover:bg-white/25 px-3 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-2 transition">
            {copied ? <Check size={14} /> : <Share2 size={14} />}{copied ? "Link copiado" : "Compartir"}
          </button>
          <Link to="/" onClick={() => track("click_login_cta")} className="bg-white text-[var(--lala-blue)] hover:bg-[var(--cream)] px-3 py-2 rounded-full text-sm font-semibold transition" data-testid="demo-login-btn">
            Solicitar demo en vivo
          </Link>
        </div>
      </div>

      {/* HERO */}
      <div className="grid lg:grid-cols-3 gap-6 mb-6">
        <div className="lg:col-span-2 sketch-border bg-[var(--cream-soft)] p-5 sm:p-7" data-testid="demo-hero">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider font-bold text-[var(--lala-blue)] mb-2">
            <Eye size={12} /> Vista de consulta · Fase 1
          </div>
          <div className="font-hand text-xl text-[var(--lala-blue)]">así te ves dentro de la familia</div>
          <h1 className="font-display text-3xl sm:text-4xl mt-1">{supplier.name}</h1>
          <p className="font-hand text-xl sm:text-2xl text-[var(--lala-blue)] mt-2">Bienvenido a tu espacio en LALA Elite — crezcamos juntos.</p>
          <p className="text-xs text-[var(--ink-soft)] mt-1 leading-relaxed max-w-2xl">
            Esta información es <b>alimentada y gobernada por Lala</b>. Tu rol en esta fase es <b>consultar tu evolución</b> y entender cómo se calcula tu evaluación.
          </p>
          <div className="mt-3 inline-flex items-center gap-2 text-[11px] bg-white border border-[var(--line)] rounded-full px-3 py-1" data-testid="demo-data-source">
            <span className="w-2 h-2 rounded-full bg-emerald-500" />
            <span className="text-[var(--ink-soft)]">Fuente de tu evaluación:</span>
            <strong className="text-[var(--ink)]">{data.data_source_label}</strong>
          </div>
          <div className="grid grid-cols-3 gap-4 mt-6">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[var(--ink-soft)]">Tu score</div>
              <div className="font-display text-4xl sm:text-5xl text-[var(--lala-blue)]" data-testid="demo-score">
                {supplier.current_score != null ? (supplier.current_score * 10).toFixed(1) : "—"}
                {supplier.current_score != null && <span className="text-lg text-[var(--ink-soft)]"> /100</span>}
              </div>
              {supplier.current_score != null && (
                <div className="text-[11px] text-[var(--ink-soft)] mt-0.5" data-testid="demo-score-10">{supplier.current_score.toFixed(2)} en escala 0–10</div>
              )}
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[var(--ink-soft)]">Nivel actual</div>
              <div className="mt-1"><TierBadge tier={supplier.current_tier} size="lg" /></div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[var(--ink-soft)]">Período</div>
              <div className="font-display text-xl sm:text-2xl">{supplier.last_period || "—"}</div>
            </div>
          </div>
        </div>
        <div>{data.badge && <DigitalSeal badge={data.badge} />}</div>
      </div>

      {/* Benchmark de categoría + distintivo */}
      <CategoryBenchmark benchmark={data.benchmark} demo />

      {/* Rationale */}
      {latest?.notes && (
        <div className="sketch-border bg-[var(--cream-soft)] p-5 sm:p-6 mb-6" data-testid="demo-rationale">
          <div className="font-hand text-lg sm:text-xl text-[var(--lala-blue)] flex items-center gap-2"><Info size={18} /> racional de tu evaluación</div>
          <h3 className="font-display text-xl sm:text-2xl mb-2">Notas del equipo Lala sobre tu período {supplier.last_period}</h3>
          <p className="text-sm text-[var(--ink)] leading-relaxed whitespace-pre-line">{latest.notes}</p>
        </div>
      )}

      {/* Radar + pillars */}
      {latest && (
        <div className="grid lg:grid-cols-2 gap-6 mb-6">
          <div className="sketch-border bg-[var(--cream-soft)] p-5 sm:p-6">
            <div className="font-hand text-lg sm:text-xl text-[var(--lala-blue)]">así vas en tus pilares</div>
            <h3 className="font-display text-xl sm:text-2xl mb-2">Tu radar 360°</h3>
            <PillarRadar pillars={latest.pillars} />
          </div>
          <div className="sketch-border bg-[var(--cream-soft)] p-5 sm:p-6">
            <div className="font-hand text-lg sm:text-xl text-[var(--lala-blue)]">detalle por pilar</div>
            <h3 className="font-display text-xl sm:text-2xl mb-3">Tu score, pilar por pilar</h3>
            <div className="space-y-3">
              {PILLARS.map((p) => {
                const v = latest.pillars[p.key] ?? 0;
                return (
                  <div key={p.key} data-testid={`demo-pillar-${p.key}`}>
                    <div className="flex justify-between items-baseline text-sm font-semibold">
                      <span>{p.label}</span>
                      <span className="text-[var(--lala-blue)] font-display text-lg">{v.toFixed(2)}</span>
                    </div>
                    <div className="h-2.5 mt-1 rounded-full bg-[var(--cream-deep)]">
                      <div className="h-2.5 rounded-full" style={{ width: `${(v / 10) * 100}%`, background: "var(--lala-blue)" }} />
                    </div>
                    <p className="text-[11px] text-[var(--ink-soft)] mt-1 leading-snug">{p.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Trend */}
      {trend.length > 1 && (
        <div className="sketch-border bg-[var(--cream-soft)] p-5 sm:p-6 mb-6">
          <div className="font-hand text-lg sm:text-xl text-[var(--lala-blue)] flex items-center gap-2"><TrendingUp size={18} /> tu evolución dentro de la familia Lala</div>
          <h3 className="font-display text-xl sm:text-2xl mb-2">Vamos en camino. Seguimos juntos.</h3>
          <div style={{ width: "100%", height: 260 }}>
            <ResponsiveContainer width="100%" height={260} minWidth={200} minHeight={160}>
              <LineChart data={trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#D9CFB9" />
                <XAxis dataKey="period" stroke="#11142B" />
                <YAxis domain={[5, 10]} stroke="#11142B" />
                <Tooltip contentStyle={{ background: "#FBF7EE", border: "1.5px solid #0033AD", borderRadius: 12 }} />
                <Line type="monotone" dataKey="score" stroke="#0033AD" strokeWidth={3} dot={{ r: 6 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Recommendations */}
      {recommendations.length > 0 && (
        <div className="sketch-border bg-[var(--cream-soft)] p-5 sm:p-6 mb-6">
          <div className="font-hand text-lg sm:text-xl text-[var(--lala-blue)] flex items-center gap-2"><Lightbulb size={18} /> recomendaciones para mejorar</div>
          <h3 className="font-display text-xl sm:text-2xl mb-3">Dónde tienes la mayor oportunidad</h3>
          <div className="grid md:grid-cols-2 gap-4">
            {recommendations.map((p) => (
              <div key={p.key} className="bg-white rounded-xl p-4 border border-[var(--line)]">
                <div className="flex items-baseline justify-between mb-1">
                  <div className="font-display text-lg text-[var(--lala-blue)]">{p.label}</div>
                  <div className="text-xs text-[var(--ink-soft)]">score <b className="text-[var(--ink)]">{p.value.toFixed(2)}</b></div>
                </div>
                <p className="text-sm text-[var(--ink-soft)] leading-relaxed">{PILLAR_ADVICE[p.key]}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Path to next tier */}
      {nextMeta && (
        <div className="sketch-border bg-[var(--cream-soft)] p-5 sm:p-6 mb-6">
          <div className="font-hand text-lg sm:text-xl text-[var(--lala-blue)] flex items-center gap-2"><Compass size={18} /> tu siguiente paso con la familia Lala</div>
          <h3 className="font-display text-xl sm:text-2xl mb-3">
            De <span style={{ color: tierMeta?.color }}>{tierMeta?.humanLabel}</span> a <span style={{ color: nextMeta.color }}>{nextMeta.humanLabel}</span>
          </h3>
          <div className="flex flex-wrap items-center gap-3 mb-3 text-sm">
            <span className="px-3 py-1 rounded-full text-white font-semibold" style={{ background: tierMeta?.color }}>{tierMeta?.humanLabel} · {tierMeta?.range}</span>
            <GemBadge tier={tierKey} size="sm" variant="pill" />
            <ArrowUpRight size={18} className="text-[var(--ink-soft)]" />
            <span className="px-3 py-1 rounded-full text-white font-semibold" style={{ background: nextMeta.color }}>{nextMeta.humanLabel} · {nextMeta.range}</span>
            <GemBadge tier={nextKey} size="sm" variant="pill" />
          </div>
          <p className="text-sm text-[var(--ink-soft)] leading-relaxed max-w-3xl">{nextMeta.description}</p>
        </div>
      )}

      {/* Tiers explainer */}
      <div className="sketch-border bg-[var(--cream-soft)] p-5 sm:p-6 mb-6">
        <div className="font-hand text-lg sm:text-xl text-[var(--lala-blue)]">los 4 niveles del programa</div>
        <h3 className="font-display text-xl sm:text-2xl mb-3">Qué significa cada nivel</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {TIER_KEYS.map((k) => {
            const m = TIER_META[k];
            const isCurrent = k === tierKey;
            return (
              <div key={k} className={`rounded-xl p-4 border ${isCurrent ? "border-[var(--lala-blue)] bg-white shadow-sm" : "border-[var(--line)] bg-white/60"}`}>
                <img src={TIER_BADGE_IMG[k]} alt={`Insignia ${m.humanLabel}`} loading="lazy"
                     className="w-20 h-20 object-contain mx-auto mb-2 drop-shadow-sm" />
                <div className="flex items-baseline justify-between">
                  <div className="font-display text-lg" style={{ color: m.color }}>{m.humanLabel}</div>
                  {isCurrent && <span className="text-[9px] uppercase tracking-wider font-bold text-[var(--lala-blue)]">Tu nivel</span>}
                </div>
                <GemBadge tier={k} size="xs" variant="minimal" className="mt-0.5" />
                <div className="text-[11px] text-[var(--ink-soft)] mb-1">{m.range}</div>
                <p className="text-xs text-[var(--ink-soft)] leading-snug">{m.description}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* CTAs */}
      <div className="grid sm:grid-cols-2 gap-4">
        <Link to="/aprendizaje" onClick={() => track("click_learning")} className="block sketch-border bg-[var(--lala-blue)] text-white p-5 hover:translate-x-[-2px] hover:translate-y-[-2px] transition-transform" data-testid="demo-learning-cta">
          <div className="flex items-center gap-3">
            <GraduationCap size={28} />
            <div className="flex-1">
              <div className="font-hand text-lg opacity-90">centro de aprendizaje</div>
              <div className="font-display text-lg sm:text-xl">Videos, infografías y FAQs</div>
            </div>
            <ArrowUpRight size={22} />
          </div>
        </Link>
        <Link to="/" onClick={() => track("click_back_landing")} className="block sketch-border bg-white p-5 hover:translate-x-[-2px] hover:translate-y-[-2px] transition-transform" data-testid="demo-back-landing">
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <div className="font-hand text-lg text-[var(--lala-blue)]">conoce el programa</div>
              <div className="font-display text-lg sm:text-xl text-[var(--ink)]">Volver al inicio de Lala Elite</div>
            </div>
            <ArrowUpRight size={22} className="text-[var(--lala-blue)]" />
          </div>
        </Link>
      </div>
    </div>
  );
}
